import { useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { useEnhancedWakeLock } from "@/hooks/voice/useEnhancedWakeLock";
import { useIPhoneTapReminder } from "@/hooks/voice/useIPhoneTapReminder";
import { useVoiceAgent } from "@/hooks/voice/useVoiceAgent";
import { getPinnedResponseLanguage } from "@/lib/agent/responseLanguage";
import { TranscriptStorageService } from "@/services/conversation/TranscriptStorageService";
import { ConversationOverrides } from "@/services/integrations/elevenlabs/VoiceAgentService";
import { Agent } from "@/types/agent/agent";
import { DEFAULT_STATUS_MESSAGES, StatusMessageVariants } from "@/types/voice/conversation-state";

interface StatusMessages {
  connecting?: string | string[];
  disconnected?: string | string[];
  speaking?: string | string[];
  listening?: string | string[];
  thinking?: string | string[];
  processing?: string | string[];
  searching?: string | string[];
  retrieving?: string | string[];
  ready?: string | string[];
}

interface UseVoiceAgentConversationOptions {
  onConversationEnd?: (conversationId: string) => void;
  errorMessage?: string;
  welcomeMessage?: string;
  customStatusMessages?: StatusMessages;
  agentData?: Agent;
  onRedirect?: (redirectUrl: string) => void;
  onShowAnalysisLoading?: () => void;
  conversationOverrides?: ConversationOverrides;
  userMemory?: string | null;
  isQuickTest?: boolean;
}

export const useVoiceAgentConversation = (
  agentId: string | undefined,
  options: UseVoiceAgentConversationOptions = {},
) => {
  const {
    onConversationEnd,
    errorMessage = "Failed to start conversation. Please check your microphone permissions and try again.",
    welcomeMessage = "Click to start",
    customStatusMessages = {},
    agentData,
    onRedirect,
    onShowAnalysisLoading,
    conversationOverrides,
    userMemory,
    isQuickTest,
  } = options;

  const responseLanguage = getPinnedResponseLanguage(agentData?.settings);

  // Memoize the options object to prevent unnecessary service recreation
  const voiceAgentOptions = useMemo(
    () => ({
      ...(conversationOverrides && { overrides: conversationOverrides }),
      ...(userMemory && { userMemory }),
      ...(isQuickTest !== undefined && { isQuickTest }),
      ...(responseLanguage && { responseLanguage }),
    }),
    [conversationOverrides, userMemory, isQuickTest, responseLanguage],
  );

  const voiceAgent = useVoiceAgent(agentId, voiceAgentOptions);
  const subscribeToDisconnect = voiceAgent.onDisconnect;
  const wakeLock = useEnhancedWakeLock();
  const tapReminder = useIPhoneTapReminder({
    onTap: () => wakeLock.reactivateAll(),
  });
  const callbacksRef = useRef({ agentData, onRedirect, onConversationEnd });
  callbacksRef.current = { agentData, onRedirect, onConversationEnd };
  const finalizedConversationIdsRef = useRef(new Set<string>());

  const finalizeConversation = useCallback(async (conversationId: string | null) => {
    if (conversationId && finalizedConversationIdsRef.current.has(conversationId)) return;
    if (conversationId) finalizedConversationIdsRef.current.add(conversationId);

    await wakeLock.releaseWakeLock();
    tapReminder.stopReminder();

    if (!conversationId) return;

    const callbacks = callbacksRef.current;
    if (callbacks.agentData?.redirect_url && callbacks.onRedirect) {
      callbacks.onRedirect(callbacks.agentData.redirect_url);
    }

    TranscriptStorageService.removeTranscript(conversationId);
    callbacks.onConversationEnd?.(conversationId);
  }, [wakeLock, tapReminder]);

  useEffect(() => {
    subscribeToDisconnect(finalizeConversation);
  }, [subscribeToDisconnect, finalizeConversation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      wakeLock.releaseWakeLock().catch((err) => {
        console.error("Error releasing wake lock on unmount:", err);
      });
      tapReminder.stopReminder();
    };
  }, [wakeLock, tapReminder]);

  const startConversation = useCallback(async (): Promise<boolean> => {
    if (voiceAgent.isConnecting || voiceAgent.connected) {
      return false;
    }

    try {
      const success = await voiceAgent.startConversation();

      if (success) {
        await wakeLock.requestWakeLock();
        tapReminder.startReminder();
        return true;
      } else {
        throw new Error("Failed to start conversation");
      }
    } catch (err) {
      console.error("Error starting conversation:", err);
      toast.error(errorMessage);
      return false;
    }
  }, [voiceAgent, wakeLock, tapReminder, errorMessage]);

  const endConversation = useCallback(async (): Promise<string | null> => {
    if (!voiceAgent.connected) {
      return null;
    }

    const conversationId = voiceAgent.getConversationId();
    await voiceAgent.endConversation();
    await finalizeConversation(conversationId);

    return conversationId;
  }, [voiceAgent, finalizeConversation]);

  const toggleConversation = useCallback(async (): Promise<string | boolean> => {
    if (voiceAgent.connected) {
      return (await endConversation()) || false;
    } else {
      return await startConversation();
    }
  }, [voiceAgent.connected, endConversation, startConversation]);

  // Merge default messages with custom ones
  const messageVariants: StatusMessageVariants = useMemo(() => {
    const mergeMessages = (defaultMsgs: string[], customMsg?: string | string[]): string[] => {
      if (!customMsg) return defaultMsgs;
      if (Array.isArray(customMsg)) return customMsg;
      return [customMsg];
    };

    return {
      connecting: mergeMessages(DEFAULT_STATUS_MESSAGES.connecting, customStatusMessages.connecting),
      disconnected: welcomeMessage ? [welcomeMessage] : DEFAULT_STATUS_MESSAGES.disconnected,
      speaking: mergeMessages(DEFAULT_STATUS_MESSAGES.speaking, customStatusMessages.speaking),
      listening: mergeMessages(DEFAULT_STATUS_MESSAGES.listening, customStatusMessages.listening),
      thinking: mergeMessages(DEFAULT_STATUS_MESSAGES.thinking, customStatusMessages.thinking),
      processing: mergeMessages(DEFAULT_STATUS_MESSAGES.processing, customStatusMessages.processing),
      searching: mergeMessages(DEFAULT_STATUS_MESSAGES.searching, customStatusMessages.searching),
      retrieving: mergeMessages(DEFAULT_STATUS_MESSAGES.retrieving, customStatusMessages.retrieving),
      ready: mergeMessages(DEFAULT_STATUS_MESSAGES.ready, customStatusMessages.ready),
    };
  }, [welcomeMessage, customStatusMessages]);

  // Track last shown message per state to avoid repeating and cache current message
  const lastMessageRef = useRef<Record<string, string>>({});
  const cachedMessageRef = useRef<string>("");
  const currentStateKeyRef = useRef<string>("");

  // Get a random message from variants, avoiding immediate repetition
  const getRandomMessage = (messages: string[], stateKey: string): string => {
    if (messages.length === 1) return messages[0];
    const lastMessage = lastMessageRef.current[stateKey];
    const availableMessages = messages.filter((m) => m !== lastMessage);
    const pool = availableMessages.length > 0 ? availableMessages : messages;
    const selected = pool[Math.floor(Math.random() * pool.length)];
    lastMessageRef.current[stateKey] = selected;
    return selected;
  };

  // Legacy statusMessages for backward compatibility
  const statusMessages = {
    connecting: messageVariants.connecting[0],
    disconnected: messageVariants.disconnected[0],
    speaking: messageVariants.speaking[0],
    listening: messageVariants.listening[0],
    thinking: messageVariants.thinking[0],
    processing: messageVariants.processing[0],
    searching: messageVariants.searching[0],
    retrieving: messageVariants.retrieving[0],
    ready: messageVariants.ready[0],
  };

  const getStatusMessage = useCallback((): string => {
    let stateKey: string;
    let messages: string[];

    if (voiceAgent.isConnecting) {
      stateKey = "connecting";
      messages = messageVariants.connecting;
    } else if (!voiceAgent.connected) {
      stateKey = "disconnected";
      messages = messageVariants.disconnected;
    } else if (voiceAgent.speaking) {
      stateKey = "speaking";
      messages = messageVariants.speaking;
    } else if (voiceAgent.listening) {
      stateKey = "listening";
      messages = messageVariants.listening;
    } else if (voiceAgent.retrieving) {
      stateKey = "retrieving";
      messages = messageVariants.retrieving;
    } else if (voiceAgent.searching) {
      stateKey = "searching";
      messages = messageVariants.searching;
    } else if (voiceAgent.processing) {
      stateKey = "processing";
      messages = messageVariants.processing;
    } else if (voiceAgent.thinking) {
      stateKey = "thinking";
      messages = messageVariants.thinking;
    } else {
      stateKey = "ready";
      messages = messageVariants.ready;
    }

    // Only get random message if state key changed
    if (currentStateKeyRef.current !== stateKey) {
      currentStateKeyRef.current = stateKey;
      cachedMessageRef.current = getRandomMessage(messages, stateKey);
    }

    return cachedMessageRef.current;
  }, [
    voiceAgent.isConnecting,
    voiceAgent.connected,
    voiceAgent.speaking,
    voiceAgent.listening,
    voiceAgent.thinking,
    voiceAgent.processing,
    voiceAgent.searching,
    voiceAgent.retrieving,
    messageVariants,
  ]);

  return {
    // Voice agent state
    ...voiceAgent,

    // Conversation controls
    startConversation,
    endConversation,
    toggleConversation,

    // Status utilities
    getStatusMessage,
    statusMessages,

    // Additional utilities
    tapReminder,
    wakeLock,
  };
};
